/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import type { TaskCardComponent } from '@tencent/ai-sidebar-sdk';
import styles from './search-images.less';
import i18n from '@i18next/i18n';
import { searchImages } from '@src/slide/application/service/ai/core/api/api';
import { SearchImageInfo } from '@src/slide/application/service/ai/core/api/api.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ISmartReplaceImageService } from '@src/slide/application/service/smart-replace-image/smart-replace-image-service.interface';

interface ITipsCardProps {
    description: string;
    confirmText: string;
    containerCount: number;
    maxPage: number;
    showLastMoreImages: boolean; // 最后一个位置是否显示4张图
}

export const getSearchImageCard = (aiReplaceImageService?: ISmartReplaceImageService) => {
    const SearchImageCard: TaskCardComponent<ITipsCardProps> = ({
        props: { description = '', confirmText = '', containerCount = 6, maxPage = 3, showLastMoreImages = true },
        onResult,
        disable,
    }) => {
        // 计算一页需要多少张图
        const getImagesNumber = () => containerCount + (showLastMoreImages ? 3 : 0);

        // 换一换功能
        const changePageIndex = (pageIndex: number) => setPageIndex(pageIndex % maxPage);

        const [displayImages, setDisplayImages] = useState<(string | null)[]>(
            Array.from({ length: getImagesNumber() }, () => ''));
        const [pageIndex, setPageIndex] = useState(0);

        // 组件内缓存每页的全量图片数据
        const imagesInfo = useRef<Record<number, SearchImageInfo[]>>([]);

        useEffect(() => {
            // pageIndex 已经有缓存，直接使用缓存不再请求
            if (imagesInfo.current[pageIndex]?.length > 0) {
                setDisplayImages(imagesInfo.current[pageIndex].map(i => i.thumb_url));
                return;
            }
            // disable 不发送请求
            if (disable) {
                setDisplayImages(Array.from({ length: getImagesNumber() }, () => null));
                return;
            }

            // 通过请求获取新图片
            setDisplayImages(Array.from({ length: getImagesNumber() }, () => ''));
            // 使用标题获取关键词
            const keyword = SlideDataServices.SlideTitleManager()?.title ?? '';
            searchImages({ keyword, num: getImagesNumber(), page: pageIndex }).then((res) => {
                setDisplayImages(res?.search_image.images.map(image => image.thumb_url) ?? []);
                imagesInfo.current[pageIndex] = res?.search_image.images ?? [];
            });
        }, [pageIndex]);

        return (
            <div
                className={classNames({
                    [styles['search-images-container']]: true,
                    [styles['search-images-container-disable']]: disable,
                })}
            >
                <div style={{ marginBottom: 8 }}>{description}</div>
                <div className={styles['images-galley']}>
                    {Array.from({ length: containerCount }).map((image, index) => {
                        const hasSubImages = index === containerCount - 1;
                        const images = hasSubImages
                            ? displayImages.slice(index, index + 4)
                            : displayImages.slice(index, index + 1);
                        return <ImageItem key={`image${index}`} hasSubImages={hasSubImages} images={images} />;
                    })}
                </div>
                <div style={{ display: 'flex' }}>
                    <button
                        className={`ai-assistant-sdk-button ai-assistant-sdk-button--primary ${styles['card-button']}`}
                        onClick={async () => {
                            // 唤起替换图片弹窗
                            await aiReplaceImageService?.showBulkReplaceDialog(
                                imagesInfo.current[pageIndex],
                                pageIndex,
                            );
                            onResult(undefined, {
                                description,
                                confirmText,
                                containerCount,
                                maxPage,
                                showLastMoreImages,
                            });
                        }}
                    >
                        <div className={styles['tick-icon']} />
                        {confirmText}
                    </button>
                    <button
                        className={`ai-assistant-sdk-button ai-assistant-sdk-button--normal ${styles['card-button']}`}
                        onClick={() => changePageIndex(pageIndex + 1)}
                    >
                        {i18n.t('换一批')}
                    </button>
                </div>
            </div>
        );
    };

    // images 有两个特殊的值，'' 的含义是请求接口中；null 的含义是直接显示空白
    const ImageItem = ({ hasSubImages, images }: { hasSubImages?: boolean; images: (string | null)[] }) => {
        const getContainerStyle = () => {
            if (hasSubImages || images.length < 1) {
                return { outline: 'none' };
            }
            if (images[0] === null) {
                return {};
            }
            return { backgroundImage: `url(${images[0]})` };
        };

        return (
            <div
                className={classNames({
                    [styles['image-item-container']]: true,
                    [styles['image-loading']]: !hasSubImages && images[0] === '',
                })}
                style={getContainerStyle()}
            >
                {hasSubImages && (
                    <div className={styles['images-sub-galley']}>
                        {images.map((image, index) => (
                            <div
                                key={`${image}${index}`}
                                className={classNames({
                                    [styles['image-item-sub-container']]: true,
                                    [styles['image-loading']]: image === '',
                                    [styles['image-sub-loading']]: true,
                                })}
                                style={image ? { backgroundImage: `url(${image})` } : {}}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return SearchImageCard;
};
